import { Box, Button, Divider, Modal } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconChevronLeft } from "@tabler/icons-react";
import { DocumentWithId } from "types/document";
import { ChatImagePreviewImageArea } from "./ChatImagePreviewImageArea";
import { ChatImagePreviewInput } from "./ChatImagePreviewInput";
import { ChatImagePreviewThumbnails } from "./ChatImagePreviewThumbnails";

const THUMBNAIL_SIZE_MOBILE = 55;
const THUMBNAIL_SIZE_DESKTOP = 75;

interface ChatImagePreviewProps {
  sendMessage: () => void;
  mutationIsLoading: boolean;
  isSending: boolean;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  textInputRef: React.RefObject<HTMLInputElement>;
  message: string;
  selectedImageIndex: number;
  setSelectedImageIndex: React.Dispatch<React.SetStateAction<number>>;
  documents: DocumentWithId[];
  removeById: (id: string) => void;
  onAddMore: (blob: Blob) => void;
  imageLimit: number;
}

const ChatImagePreview: React.FC<ChatImagePreviewProps> = ({
  sendMessage,
  mutationIsLoading,
  isSending,
  handleChange,
  handleKeyDown,
  textInputRef,
  message,
  selectedImageIndex,
  setSelectedImageIndex,
  documents,
  removeById,
  onAddMore,
  imageLimit,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isDesktop = isMobile === false;
  const thumbnailSize = isDesktop ? THUMBNAIL_SIZE_DESKTOP : THUMBNAIL_SIZE_MOBILE;
  const canSend = message !== "" || documents.length > 0;

  return (
    <Modal
      opened={selectedImageIndex !== -1}
      onClose={() => setSelectedImageIndex(-1)}
      padding={0}
      radius={0}
      zIndex={20000}
      fullScreen
      withCloseButton={isDesktop}
      styles={{
        content: {
          backgroundColor: "#000000",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
        header: isDesktop
          ? { backgroundColor: "#000000", padding: "12px 16px", flexShrink: 0 }
          : { display: "none" },
        close: { color: "white" },
        body: {
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          overscrollBehavior: "contain",
          backgroundColor: "#000000",
        },
      }}
    >
      {!isDesktop && (
        <Box
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            height: 100,
            padding: "0 25px",
            flexShrink: 0,
            borderBottom: "1px solid #333",
          }}
        >
          <Button
            onClick={() => setSelectedImageIndex(-1)}
            className="text-white w-10 h-10 flex items-center justify-center rounded-full bg-blue"
          >
            <IconChevronLeft size={24} color="white" />
          </Button>
        </Box>
      )}

      {isDesktop && <Divider />}

      <ChatImagePreviewImageArea
        documents={documents}
        selectedImageIndex={selectedImageIndex}
        setSelectedImageIndex={setSelectedImageIndex}
        isDesktop={isDesktop}
      />

      <ChatImagePreviewThumbnails
        documents={documents}
        selectedImageIndex={selectedImageIndex}
        setSelectedImageIndex={setSelectedImageIndex}
        removeById={removeById}
        onAddMore={onAddMore}
        imageLimit={imageLimit}
        isDesktop={isDesktop}
        thumbnailSize={thumbnailSize}
      />

      <ChatImagePreviewInput
        sendMessage={sendMessage}
        mutationIsLoading={mutationIsLoading}
        isSending={isSending}
        handleChange={handleChange}
        handleKeyDown={handleKeyDown}
        textInputRef={textInputRef}
        message={message}
        canSend={canSend}
        isDesktop={isDesktop}
      />
    </Modal>
  );
};

export default ChatImagePreview;
